import { computed, effect, inject, resource, signal, untracked } from '@angular/core';
import { Command } from '@workbuddy/ui/model';
import { ActivityEntityName, EntityView, EntityViewBoard, EntityViewCalendar, EntityViewCard, EntityViewCard2, EntityViewDisplay, EntityViewKanban, EntityViewKanbanColumn, EntityViewSortDirection, EntityViewVisibility, MetadataEntityFieldData } from '@jms/model';
import { FilterFieldProvider } from '@workbuddy/ui/v2/blocks/filter';
import { SettingsPanelBuilder, SettingsPanelForm, SettingsPanelOptionId, buildDisplayPanel, countFilterRules, splitPinnedSelection, toFieldOptions, toFieldOptionsWithNone } from '@workbuddy/ui/v2/blocks/settings.panel';
import { EntityFilterFieldProvider } from '../../../providers';
import { resolveCalendarDateFields, resolveCardFields, resolveDefaultRecordScope, resolveFilterFields, resolveRecordScopeOptions, resolveTableColumnFields, resolveUiFields } from '../../../state/features/with.metadata';
import { ganttFormToConfig } from './gantt.display.adapter';
import { calendarFormToConfig, resolveCalendarPanelOptions, type CalendarDisplayFormValues } from './calendar.display.adapter';
// Deep path, not the barrel: `board.kanban`'s barrel pulls in the board component, which imports the
// store, whose view/display-settings features import this editor back — the cycle leaves the editor's
// injectables undefined at boot. The service file itself imports nothing from the board.
import { KanbanValueSetService } from '../../board.kanban/kanban.value.set.service';
import { resolveDisplayTypes } from './resolve.display.types';
import { resolveGroupFields } from './resolve.group.fields';
import { resolveKanbanColumnFields } from './resolve.kanban.column.fields';
import { resolveKanbanColumnSeed } from './resolve.kanban.column.seed';
import { reconcileGroupSelection, resolveColumnGroup, toColumnPickerOptions, toKanbanColumns } from './resolve.kanban.column.values';
import { ViewAdvancedFilterPanelComponent } from '../view.advanced.filter.panel.component';
import { ViewSettingsConfig, ViewSettingsValue } from '../view.settings.model';
import { ViewAdapterContext, ViewAdapterDeps, ViewStepAdapter } from './view.step.adapter';
import { boardPageSizeOptions, clampUnvirtualizedBoardPageSize } from '../../../board.page.size';

/**
 * Terminal step — the full view configuration form. Builds against the
 * resolved scope (or primary if no secondary filter exists) and emits a
 * fully merged `Partial<EntityView>` via `buildValue` when the user saves.
 */
export function createConfigureAdapter(deps: ViewAdapterDeps): ViewStepAdapter {

  // Private filter provider for the wizard's filter sub-panel. Configured
  // reactively via the effect below so it stays in sync with the user's
  // recordScope choice. Independent of the board's shared filter provider
  // (which drives the header filter bar).
  const filterProvider = new EntityFilterFieldProvider();

  // The board provides this, so the editor reads the same cached value set the board is rendering from
  // instead of re-fetching the picker as soon as someone opens Edit View.
  const valueSetService = inject(KanbanValueSetService, { optional: true });

  /** The column field the user has currently chosen — the value set and the groupings follow it. */
  const $columnField = signal<MetadataEntityFieldData | null>(null);

  const columnValueSet = resource({
    params: () => ({ field: $columnField(), entity: deps.$config()?.metadata?.type ?? null }),
    loader: ({ params }) => valueSetService?.load(params.field, params.entity as ActivityEntityName | null) ?? Promise.resolve(null),
  });

  const $columnGroup = computed(() => resolveColumnGroup($columnField()));
  const $columnValueSet = computed(() => columnValueSet.value() ?? null);
  const $columnSelection = signal<SettingsPanelOptionId[]>([]);
  const $columnValueOptions = computed(() => toColumnPickerOptions($columnValueSet(), $columnGroup(), $columnSelection()));

  /**
   * The live form, so the Columns picker can be re-seeded after `build()`.
   *
   * The value set loads asynchronously and the column FIELD can change, but the form is built once and
   * cached by the wizard — a `selected` array snapshotted at build time is therefore empty on the first
   * paint and stale for the rest of the session. Holding the form lets the seeding effect below write
   * the derived selection into the control instead.
   */
  let form: SettingsPanelForm | null = null;
  /** The column field the tracked selection was derived for, so a field CHANGE re-seeds from scratch. */
  let seededColumnPath: string | null = null;

  // Reactively reconfigure the filter provider whenever the active scope
  // or metadata changes. Mirrors the queue flag adapter's factory-body
  // effect pattern — keeps signal writes out of `build()` (which runs
  // inside the wizard's `panel` computed).
  effect(() => {

    const metadata = deps.$config()?.metadata;
    if (!metadata) return;
    const scope = deps.$recordScope() ?? resolveDefaultRecordScope(metadata, deps.$config()?.recordScopes);
    const filterableFields = resolveFilterFields(metadata, scope);
    filterProvider.configureFields(metadata.type ?? null, filterableFields);
  });

  /**
   * Seeds the Columns picker whenever the value set resolves or the column FIELD changes.
   *
   * Two things make this an effect rather than a `build()` snapshot. The value set is loaded
   * asynchronously, so at build time it is null and the derived selection is empty — the picker would
   * open with nothing ticked on a view that is on the default. And the saved view's columns describe
   * the field it was saved against: once the user picks a different field those ids and rules mean
   * nothing, so the selection falls back to the new field's whole value set (the default, all ticked).
   *
   * A signal write belongs here, not in `build()` — that runs inside the wizard's `panel` computed,
   * where writing a signal is a change-after-checked hazard.
   */
  effect(() => {

    const valueSet = $columnValueSet();
    const group = $columnGroup();
    const field = $columnField();
    const path = field ? field.path ?? field.name ?? null : null;

    untracked(() => {

      const { selection, resolved } = resolveKanbanColumnSeed(path, deps.$config()?.view?.kanban, valueSet, group);

      $columnSelection.set(selection);

      // Nothing to seed until the set is known: writing [] here would read as "no columns" and clear
      // the saved configuration the moment the editor opened. A field CHANGE is the exception — the
      // old field's ticks must go even before the new set lands.
      if (!resolved && path === seededColumnPath) return;

      // A different field is a different configuration, so the switch falls back to Automatic: the
      // list the user customised described values this field does not have.
      if (path !== seededColumnPath) form?.update('kanban.customiseColumns', resolveCustomiseDefault(path));
      seededColumnPath = path;

      if (!form) return;
      if (sameSelection(form.get('kanban.columns') as SettingsPanelOptionId[] | undefined, selection)) return;
      form.update('kanban.columns', selection);
    });
  });

  /** Customise starts ON only for the field whose saved view already carries an explicit column list. */
  const resolveCustomiseDefault = (path: string | null): boolean => {

    const saved = deps.$config()?.view?.kanban;
    const savedPath = saved?.column ? String(saved.column) : null;
    return !!path && path === savedPath && !!saved?.columns?.length;
  };

  // Follows the Column dropdown, so choosing a field loads its value set and the Columns editor is
  // populated by the time the user reaches it.
  effect(() => {

    const metadata = deps.$config()?.metadata;
    const path = deps.$selectedColumn();
    if (!metadata || !path || path === 'none') {
      $columnField.set(null);
      return;
    }

    const scope = deps.$recordScope() ?? resolveDefaultRecordScope(metadata, deps.$config()?.recordScopes);
    $columnField.set(resolveUiFields(metadata, scope).find(field => (field.path ?? field.name) === path) ?? null);
  });


  /** Effective record scope — explicit on model, or the metadata-default
   *  if missing (covers legacy views saved before recordScope existed). */
  const resolveEffectiveScope = (model: Partial<EntityView>) => deps.$config()?.recordScopes?.length === 1
    ? resolveDefaultRecordScope(deps.$config()?.metadata, deps.$config()?.recordScopes)
    : model.recordScope ?? resolveDefaultRecordScope(deps.$config()?.metadata, deps.$config()?.recordScopes);

  /** Scope-aware field lists. The board's store-level `$uiFields` etc.
   *  ignore scope (they're used elsewhere); the wizard recomputes them
   *  locally so users only see fields valid for their chosen scope. */
  const resolveScopedFields = (model: Partial<EntityView>) => {

    const metadata = deps.$config()?.metadata;
    const scope = resolveEffectiveScope(model);
    const uiFields = resolveUiFields(metadata, scope);
    return {
      uiFields,
      sortableFields: uiFields.filter(f => f.sortable),
      groupableFields: resolveKanbanColumnFields(uiFields),
      rowFields: resolveGroupFields(metadata, scope, uiFields),
      cardFields: resolveCardFields(uiFields, metadata?.isFinancialDocument ?? false),
    };
  };

  /** Friendly label for the current scope, taken from the same pluralized
   *  source the setup gate uses. Empty when there's no secondary option. */
  const resolveScopeLabel = (model: Partial<EntityView>): string => {

    const scope = resolveEffectiveScope(model);
    if (!scope) return '';
    return resolveRecordScopeOptions(deps.$config()?.metadata, deps.$config()?.recordScopes).find(o => o.id === scope)?.label ?? '';
  };

  /**
   * The column definitions the picker's selection means, or none while the value set is still catching
   * up with the Column dropdown.
   *
   * The value set is loaded per field, so between the user choosing a new Column and its set arriving
   * the picker's ids still name the OLD field's values. Reading them then would stamp the previous
   * field's columns onto the new one; the seeding effect re-ticks the picker a moment later, so the
   * board simply previews the default in the meantime.
   */
  const resolveFormColumns = (kanban: Record<string, any> | null | undefined): EntityViewKanbanColumn[] | undefined => {

    // Automatic is the absence of a column list, not a list that happens to name every value — only
    // an absent one keeps claiming the values a tenant adds after this view is saved.
    if (kanban?.customiseColumns !== true) return undefined;

    const field = $columnField();
    const chosen = deps.$selectedColumn();
    const path = field ? field.path ?? field.name ?? null : null;
    if (!path || path !== chosen) return undefined;

    return toKanbanColumns(kanban?.columns as SettingsPanelOptionId[], $columnValueSet(), $columnGroup());
  };

  const buildValue = (formValues: Record<string, unknown>, model: Partial<EntityView>): Partial<EntityView> => {

    const config = deps.$config();
    const values = formValues as Record<string, any>;
    const fields = resolveScopedFields(model).uiFields;

    const toViewFields = (ids: string[]) => (ids ?? []).map(id => {
      const f = fields.find(f => (f.path ?? f.name) === id);
      return { _id: id, name: f?.displayName ?? f?.name ?? id, visible: true, systemHidden: false };
    });

    // Pinned rows of the one Columns picker are the fixed columns
    const columns = splitPinnedSelection(values.list?.columns);

    const view: Partial<EntityView> = {
      ...model,
      // Backfill recordScope when missing on legacy views — preserves
      // the current implicit behavior explicitly so subsequent reads
      // don't need to fall back to resolveDefaultRecordScope.
      recordScope: resolveEffectiveScope(model),
      pageSize: clampUnvirtualizedBoardPageSize(Number(values.pageSize) || config?.pageSize || 50, values.display),
      display: values.display,
      visibility: values.visibility,
      name: values.name,
      sharedWith: values.sharedWith,
      card2: normalizeCard2Config(model.card2, values.card2),
      board: normalizeDisplayConfig(model.board, values.board) as EntityViewBoard,
      // Calendar is only stamped on a Calendar view: switching to it seeds a renderable default
      // (Month, all-day, first date field, the card title as the chip field); switching away leaves
      // whatever the model already held so the config survives a round trip through another display.
      calendar: values.display === EntityViewDisplay.Calendar
        ? calendarFormToConfig(model.calendar, values.calendar as CalendarDisplayFormValues, { dateFields: resolveCalendarDateFields(fields), uiFields: fields, titleField: resolveCalendarTitleField(model) })
        : model.calendar as EntityViewCalendar,
      gantt: values.gantt || values.display === EntityViewDisplay.Gantt ? ganttFormToConfig(model.gantt, values.gantt, fields) : model.gantt,
      kanban: normalizeKanbanConfig(model.kanban, values.kanban, resolveFormColumns(values.kanban)) as EntityViewKanban,
      list: {
        ...model.list,
        identifier: values.listView?.identifier !== 'none' ? values.listView?.identifier : null,
        title: values.listView?.title !== 'none' ? values.listView?.title : null,
        status: values.listView?.status !== 'none' ? values.listView?.status : null,
        fields: values.listView?.fields ?? [],
        groupBy: normalizeGroupByValue(values.listView?.groupBy, (model.list as { groupBy?: string | null })?.groupBy),
        sortBy: values.listView?.sortBy !== 'none' ? values.listView?.sortBy : null,
        sortOrder: values.listView?.sortOrder ?? EntityViewSortDirection.Asc,
      },
      table: {
        ...model.table,
        other: toViewFields(columns.unpinned as string[]),
        fixed: toViewFields(columns.pinned as string[]),
        // Prefer form value; fall back to model so other field edits don't wipe groupBy
        groupBy: normalizeGroupByValue(values.list?.groupBy, (model.table as { groupBy?: string | null })?.groupBy),
      },
      sort: values.list?.sortBy && values.list?.sortBy !== 'none' ? { _id: values.list.sortBy, direction: values.list.sortOrder } : null,
      advancedFilter: values.filter,
    } as Partial<EntityView>;

    return view;
  };

  const buildPreviewValue = (formValues: Record<string, unknown>, model: Partial<EntityView>): ViewSettingsValue => {

    const config = deps.$config();
    const view = buildValue(formValues, model);
    const groupBy = (view.table as { groupBy?: string | null } | undefined)?.groupBy ?? null;
    return {
      view,
      pageSize: clampUnvirtualizedBoardPageSize(Number((formValues as any).pageSize) || config?.pageSize || 50, view.display),
      // Echo groupBy onto request so items preview uses server group-complete paging
      request: { groupBy, subGroupBy: (view.table as { subGroupBy?: string | null })?.subGroupBy ?? null },
    };
  };

  return {
    buildValue,
    build: (ctx, model, wizard) => {

      const config = deps.$config();
      const { uiFields, cardFields, sortableFields, groupableFields, rowFields } = resolveScopedFields(model);
      // Base displays only: persisted views and server templates are normalized at the board
      // boundary before they can seed this model; a missing display defaults to Table.
      const display = model.display ?? EntityViewDisplay.Table;
      const card2 = model.card2;
      const kanban = model.kanban;

      // Scope-aware dedup option signals — built locally because the
      // scoped field lists come from `model.recordScope` which is only
      // visible at build time.
      const $columnOptions = computed(() => toFieldOptionsWithNone(groupableFields).filter(o => o.id === 'none' || o.id !== deps.$selectedRow()));
      const $rowOptions = computed(() => toFieldOptionsWithNone(rowFields).filter(o => o.id === 'none' || o.id !== deps.$selectedColumn()));
      const tableColumnOptions = toFieldOptions(resolveTableColumnFields(uiFields));
      // A saved column the table no longer offers (an attachment, embedded lines) is dropped here, so the next save removes it
      const tableColumnIds = new Set(tableColumnOptions.map(o => o.id));
      const offeredColumns = (fields: { _id: unknown }[] | undefined) => (fields ?? []).map(f => f._id as string).filter(id => tableColumnIds.has(id));

      const sortableOptions = toFieldOptionsWithNone(sortableFields);
      // Table Group takes the same pool as the Kanban Row axis: parents, then row-grain groupable
      // fields. A Stages board is no longer pinned to None | Job — the server pages whole groups for
      // a row-level groupBy too (`mongo.row.group.paging.ts`), so a stage field is a real group.
      const groupableOptions = toFieldOptionsWithNone(rowFields);

      const cardFieldOptions = {
        identifier: toFieldOptionsWithNone(cardFields.identifier),
        status: toFieldOptionsWithNone(cardFields.status),
        title: toFieldOptionsWithNone(cardFields.title),
        description: toFieldOptionsWithNone(cardFields.description),
        body: toFieldOptions(cardFields.body),
        footer: toFieldOptions(cardFields.footer),
      };

      const isFinancialDocument = config?.metadata?.isFinancialDocument ?? false;
      // Financial documents repurpose the card Status slot as the hero "Amount" (Currency options).
      const cardStatusLabel = isFinancialDocument ? 'Amount' : 'Status';

      // Board layouts offered for this entity come from its manifest declaration (e.g. financial
      // documents support Card and Table). No declaration means Table only, plus the view's own
      // display so a legacy layout stays selectable.
      const displayTypes: EntityViewDisplay[] = resolveDisplayTypes(config?.supportedDisplays, display as EntityViewDisplay);

      const scopeLabel = resolveScopeLabel(model);
      const hasScope = resolveRecordScopeOptions(config?.metadata, config?.recordScopes).length > 0;

      const builder = new SettingsPanelBuilder(model._id ? 'Edit View' : 'Create View')
        .input('name', 'Name', { placeholder: 'View name', value: model.name ?? '' })
        .tabs('visibility', 'View Access', {
          commands: [
            new Command({ _id: EntityViewVisibility.Private, label: 'Private' }),
            new Command({ _id: EntityViewVisibility.Shared, label: 'Shared' }),
          ],
          selected: model.visibility ?? EntityViewVisibility.Private,
        })
        .entityPicker('sharedWith', 'Shared With', {
          entity: [ActivityEntityName.ContactEmployee, ActivityEntityName.Team],
          mode: 'multi',
          selected: model.sharedWith?.map(e => e._id) ?? [],
          groupLabel: (entity) => entity === ActivityEntityName.Team ? 'Team' : 'Employees',
          hidden: (values) => values.visibility !== EntityViewVisibility.Shared,
          displayValue: ({ field }) => computed(() => { const v = field() as any[]; return !v?.length ? 'Select members' : `${v.length} member${v.length === 1 ? '' : 's'}`; }),
        })
        .input('recordScopeDisplay', 'View shows', {
          value: scopeLabel,
          disabled: () => true,
          hidden: !hasScope,
        })
        .divider()
        .navigate('filter', 'Filter', {
          component: ViewAdvancedFilterPanelComponent,
          initialValue: model.advancedFilter ?? {},
          displayValue: ({ field }) => computed(() => {
            const count = countFilterRules(field());
            return count === 0 ? 'Configure filters' : `${count} rule${count === 1 ? '' : 's'}`;
          }),
          navigateOptions: { providers: [{ provide: FilterFieldProvider, useValue: filterProvider }] },
        })
        .dropdown('pageSize', 'Page Size', {
          selected: clampUnvirtualizedBoardPageSize(config.pageSize ?? 50, model.display),
          options: boardPageSizeOptions(model.display, 'configure'),
        })
        .divider();

      buildDisplayPanel(builder, {
        types: displayTypes,
        selected: display as EntityViewDisplay,
        sortableOptions,
        groupableOptions,
        card: {
          card: cardFieldOptions,
          sortableOptions,
          statusLabel: cardStatusLabel,
          identifier: card2?.card?.identifier ?? 'none',
          status: card2?.card?.status ?? 'none',
          title: card2?.card?.title ?? 'none',
          description: card2?.card?.description ?? 'none',
          body: card2?.card?.body ?? [],
          footer: card2?.card?.footer ?? [],
          sortBy: card2?.sortBy ?? 'none',
          sortOrder: card2?.sortOrder ?? EntityViewSortDirection.Asc,
        },
        kanban: {
          card: cardFieldOptions,
          sortableOptions,
          statusLabel: cardStatusLabel,
          identifier: kanban?.card?.identifier ?? 'none',
          status: kanban?.card?.status ?? 'none',
          title: kanban?.card?.title ?? 'none',
          description: kanban?.card?.description ?? 'none',
          body: kanban?.card?.body ?? [],
          footer: kanban?.card?.footer ?? [],
          columnOptions: $columnOptions,
          rowOptions: $rowOptions,
          column: kanban?.column ?? 'none',
          row: kanban?.row ?? 'none',
          columnValueOptions: $columnValueOptions,
          selectedColumnValues: untracked($columnSelection),
          customiseColumns: !!kanban?.columns?.length,
          sortBy: kanban?.sortBy ?? 'none',
          sortOrder: kanban?.sortOrder ?? EntityViewSortDirection.Asc,
        },
        gantt: { fieldOptions: toFieldOptions(uiFields), dateOptions: toFieldOptions(resolveCalendarDateFields(uiFields)), value: ganttFormToConfig(model.gantt, undefined, uiFields) },
        calendar: resolveCalendarPanelOptions(model, uiFields, config?.supportedCalendarViews, config?.calendarResourceSelection),
        table: {
          columnOptions: tableColumnOptions,
          selectedColumns: offeredColumns(model.table?.other),
          selectedFixedColumns: offeredColumns(model.table?.fixed),
          groupBy: model.table?.groupBy ?? 'none',
          sortBy: model.sort?._id ?? 'none',
          sortOrder: model.sort?.direction ?? EntityViewSortDirection.Asc,
        },
      });

      const isSystem = model.system === true && !model.shadowed;
      const canDelete = !!model._id && !isSystem && !model.shadowed;

      if (model.shadowed) {
        builder.footerLeft(new Command({ label: 'Reset to default', action: () => ctx.onReset() }));
      } else if (canDelete) {
        builder.footerLeft(new Command({ label: 'Delete', action: () => ctx.onDelete() }));
      }

      builder.footerRight(new Command({ label: 'Cancel', action: () => ctx.onCancel() }));

      const isCreate = (config?.mode ?? (model._id ? 'edit' : 'create')) === 'create';

      if (isCreate) {
        builder.footerSplitButton(new Command({ label: 'Save View', action: () => wizard.next() }), []);
      } else if (isSystem) {
        builder.footerSplitButton(new Command({ label: 'Save as new view', action: () => ctx.onSaveAsNew() }), []);
      } else {
        builder.footerSplitButton(new Command({ label: 'Save View', action: () => wizard.next() }), [new Command({ label: 'Save as new view', action: () => ctx.onSaveAsNew() })]);
      }

      form = builder.build();

      form.onUpdate((path, value) => {
        // Async column seeding can reach the cached form after the wizard's source changed but
        // before its lazy panel rebuilt. That form belongs to the old model, not the visible view.
        if (wizard.model() !== model) return;

        if (path === 'kanban.column' || path === 'kanban.row') {
          ctx.onSelectionChange(path, value);
        }
        if (path === 'kanban.columns') {
          // Ticking a group header greys its members; unticking it must hand them back, or the group
          // ends up with nothing selected and no clickable row to undo it.
          const reconciled = reconcileGroupSelection($columnSelection(), value as SettingsPanelOptionId[], $columnValueSet(), $columnGroup());
          $columnSelection.set(reconciled);
          if (!sameSelection(value as SettingsPanelOptionId[], reconciled)) {
            form!.update('kanban.columns', reconciled);
            return;
          }
        }
        const preview = buildPreviewValue(form!.value() as Record<string, unknown>, model);
        ctx.onPreview(preview);
        ctx.onPreviewView(preview);
        ctx.onPreviewRequest(preview);
      });

      return form;
    },
  };
}

/** Order-insensitive selection compare, so re-seeding does not fire a form update that changes nothing. */
function sameSelection(a: SettingsPanelOptionId[] | null | undefined, b: SettingsPanelOptionId[] | null | undefined): boolean {

  const left = (a ?? []).map(id => String(id));
  const right = (b ?? []).map(id => String(id));
  if (left.length !== right.length) return false;

  const seen = new Set(left);
  return right.every(id => seen.has(id));
}

function normalizeDisplayConfig(existing: Partial<EntityViewBoard> | Partial<EntityViewKanban> | null | undefined, value: Record<string, any> | null | undefined): Partial<EntityViewBoard> | Partial<EntityViewKanban> | null {

  if (!existing && !value) return null;
  return {
    ...existing,
    ...value,
    card: normalizeCardConfig(existing?.card, value?.card) as EntityViewCard,
    column: value?.column === 'none' ? null : value?.column ?? existing?.column ?? null,
    row: value?.row === 'none' ? null : value?.row ?? existing?.row ?? null,
    sortBy: value?.sortBy === 'none' ? null : value?.sortBy ?? existing?.sortBy ?? null,
    sortOrder: value?.sortOrder ?? existing?.sortOrder ?? EntityViewSortDirection.Asc,
  } as Partial<EntityViewBoard> | Partial<EntityViewKanban>;
}

/**
 * Kanban config plus its column definitions.
 *
 * A pristine default writes an EMPTY list, which the board and the model both read as "follow the
 * generator" — so a status added to the tenant tomorrow shows up on its own, and freezing today's
 * value set into the view the first time someone opens the editor never happens. It has to be an empty
 * list rather than an absent key: the preview overlay merges kanban key by key, so omitting `columns`
 * would leave the SAVED columns standing and narrowing back to the default would not preview.
 *
 * Columns are also dropped outright when the column FIELD changes away from what the existing config
 * was written against — those ids and rules name a value set that is no longer on screen.
 */
function normalizeKanbanConfig(existing: Partial<EntityViewKanban> | null | undefined, value: Record<string, any> | null | undefined, columns: EntityViewKanbanColumn[] | undefined): Partial<EntityViewKanban> | null {

  const base = normalizeDisplayConfig(existing, value) as Partial<EntityViewKanban> | null;
  if (!base) return null;

  // `columns` on the form is the PICKER's selection and `customiseColumns` its switch — both are
  // editor state, never view shape. Drop them and write what the selection MEANS instead, or the empty
  // default when the view is on Automatic.
  const { columns: _selection, customiseColumns: _switch, ...rest } = base as Record<string, unknown>;

  return { ...rest, columns: columns ?? [] } as Partial<EntityViewKanban>;
}

function normalizeCardConfig(existing: Partial<EntityViewCard> | null | undefined, value: Record<string, any> | null | undefined): Partial<EntityViewCard> | null {

  if (!existing && !value) return null;
  return {
    ...existing,
    ...value,
    identifier: value?.identifier === 'none' ? null : value?.identifier ?? existing?.identifier ?? null,
    status: value?.status === 'none' ? null : value?.status ?? existing?.status ?? null,
    title: value?.title === 'none' ? null : value?.title ?? existing?.title ?? null,
    description: value?.description === 'none' ? null : value?.description ?? existing?.description ?? null,
    body: value?.body ?? existing?.body ?? [],
    footer: value?.footer ?? existing?.footer ?? [],
  } as Partial<EntityViewCard>;
}

function normalizeCard2Config(existing: Partial<EntityViewCard2> | null | undefined, value: Record<string, any> | null | undefined): Partial<EntityViewCard2> | null {

  if (!existing && !value) return null;
  return {
    ...existing,
    card: normalizeCardConfig(existing?.card, value?.card) as EntityViewCard,
    sortBy: value?.sortBy === 'none' ? null : value?.sortBy ?? existing?.sortBy ?? null,
    sortOrder: value?.sortOrder ?? existing?.sortOrder ?? EntityViewSortDirection.Asc,
  } as Partial<EntityViewCard2>;
}

/** Form 'none' / missing → null; undefined form field keeps fallback (don't wipe model). */
function normalizeGroupByValue(formValue: unknown, fallback?: string | null): string | null {

  if (formValue === 'none') return null;
  if (formValue === undefined || formValue === '') return fallback ?? null;
  return formValue as string;
}

/** Card / list title the calendar seeds its first chip field from, so a fresh Calendar view is not blank. */
function resolveCalendarTitleField(model: Partial<EntityView>): string | null {

  const title = model.card2?.card?.title ?? model.kanban?.card?.title ?? model.list?.title;
  return typeof title === 'string' ? title : null;
}
